import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Redirect, useLocation, useSearchParams } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { Alert, AlertActions, AlertDescription, AlertTitle, } from "catalyst/alert";
import { Badge } from "catalyst/badge";
import { Button } from "catalyst/button";
import { FingerprintIcon } from "../components/BrandIcons.js";
import { LoginRedirectErrorAlert } from "../components/LoginRedirectErrorAlert.js";
import { EmailAuthForm } from "../components/EmailAuthForm.js";
import { Strong, Text, TextLink } from "catalyst/text";
import { apiStartEmailAuthentication, apiFinishEmailAuthentication, hasPendingInviteCookie, useProfile, } from "../lib/api.js";
import { loginWithPasskey } from "../lib/passkey.js";
import { addEmailLoginChallenge, emailErrorMessage, } from "../lib/email.js";
import { ApiError, bumpEpoch } from "../lib/util.js";
const loginErrorMessage = (err) => {
    if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        return "Этот passkey не зарегистрирован в системе";
    }
    const message = err instanceof Error ? err.message : String(err);
    return message;
};
export const withReturnTo = (path, returnTo) => returnTo ? `${path}?${new URLSearchParams({ return_to: returnTo })}` : path;
/** Confirm the session before redirecting; `logged_in` may outlive it. */
export const ContinueLogin = ({ returnTo }) => {
    const { data: profile, error } = useProfile();
    useEffect(() => {
        if (profile)
            window.location.replace(withReturnTo("/hub/api/login/continue", returnTo));
    }, [profile, returnTo]);
    if (error && !(error instanceof ApiError && error.status === 401)) {
        return _jsx(Redirect, { to: "/", replace: true });
    }
    return null;
};
const PendingInviteLoginBadge = () => (_jsx(Badge, { color: "lime", className: "px-8! py-6! font-normal! flex flex-col items-start! gap-3 shadow-sm", children: _jsx("p", { children: "\u0427\u0442\u043E\u0431\u044B \u043F\u0440\u0438\u0441\u043E\u0435\u0434\u0438\u043D\u0438\u0442\u044C\u0441\u044F \u043A \u0445\u0430\u0431\u0443, \u0441\u043D\u0430\u0447\u0430\u043B\u0430 \u0432\u043E\u0439\u0434\u0438\u0442\u0435 \u0432 \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0438\u0439 \u0430\u043A\u043A\u0430\u0443\u043D\u0442 \u0438\u043B\u0438 \u0441\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043D\u043E\u0432\u044B\u0439." }) }));
const Login = () => {
    const [, navigate] = useLocation();
    const [searchParams] = useSearchParams();
    const historyState = useHistoryState();
    const [submitting, setSubmitting] = useState(false);
    const [pendingEmailLogin, setPendingEmailLogin] = useState(null);
    const [email, setEmail] = useState("");
    const mounted = useRef(false);
    const [emailSubmitting, setEmailSubmitting] = useState(false);
    const challenge = pendingEmailLogin?.challenge ?? null;
    const showCode = challenge !== null && historyState?.emailLoginCode === true;
    const [emailError, setEmailError] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const loginError = searchParams.get("error");
    const returnTo = searchParams.get("return_to");
    const showPendingInviteNotice = hasPendingInviteCookie();
    useEffect(() => {
        mounted.current = true;
        // A reload starts at the email step, even on a former code history entry.
        if (window.history.state?.emailLoginCode) {
            window.history.replaceState(null, "");
        }
        return () => {
            mounted.current = false;
        };
    }, []);
    useEffect(() => {
        setEmailError(null);
    }, [showCode]);
    const onClickPasskeyLogin = async () => {
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await loginWithPasskey();
            bumpEpoch();
        }
        catch (err) {
            console.error(err);
            setErrorMsg(loginErrorMessage(err));
            setSubmitting(false);
        }
    };
    const requestEmailCode = async () => {
        const normalizedEmail = email.trim().toLowerCase();
        if (emailSubmitting || submitting || !normalizedEmail)
            return;
        setEmail(normalizedEmail);
        setEmailSubmitting(true);
        setEmailError(null);
        try {
            const challenge = await apiStartEmailAuthentication(normalizedEmail);
            if (!mounted.current)
                return;
            setPendingEmailLogin((previous) => addEmailLoginChallenge(normalizedEmail, challenge, previous));
            navigate(window.location.pathname + window.location.search, {
                state: { emailLoginCode: true },
            });
        }
        catch (err) {
            if (mounted.current)
                setEmailError(emailErrorMessage(err));
        }
        finally {
            if (mounted.current)
                setEmailSubmitting(false);
        }
    };
    const finishEmailLogin = async (code) => {
        if (emailSubmitting ||
            submitting ||
            !pendingEmailLogin ||
            !/^[0-9]{6}$/.test(code))
            return;
        setEmailSubmitting(true);
        setEmailError(null);
        try {
            await apiFinishEmailAuthentication(pendingEmailLogin.challenge.challenge_id, code, pendingEmailLogin.email, pendingEmailLogin.previousChallengeIds);
            bumpEpoch();
        }
        catch (err) {
            setEmailError(emailErrorMessage(err));
            setEmailSubmitting(false);
        }
    };
    return (_jsxs(_Fragment, { children: [showPendingInviteNotice && (_jsx("div", { className: "mr-2 ms-1.5", children: _jsx(PendingInviteLoginBadge, {}) })), _jsx(LoginRedirectErrorAlert, { error: loginError }), _jsxs("div", { className: "grid w-full max-w-72 gap-2", children: [_jsxs(Button, { className: "justify-self-start", color: "light", disabled: submitting || emailSubmitting, onClick: onClickPasskeyLogin, type: "button", children: [_jsx(FingerprintIcon, {}), _jsx("span", { children: "\u0412\u043E\u0439\u0442\u0438 \u043F\u043E passkey" })] }), _jsx(Text, { children: "\u0438\u043B\u0438" }), _jsx(EmailAuthForm, { email: email, emailLabel: "Email \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430", challenge: showCode ? challenge : null, submitting: emailSubmitting || submitting, error: emailError, buttonLabel: "\u0412\u043E\u0439\u0442\u0438", onEmailChange: setEmail, onClearError: () => setEmailError(null), onSubmit: (value) => {
                            if (showCode)
                                void finishEmailLogin(value);
                            else
                                void requestEmailCode();
                        } })] }), _jsx("div", { children: _jsxs(Text, { className: "text-left", children: ["\u0418\u043B\u0438 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0439\u0442\u0435 ", _jsx(Strong, { children: "\u0441\u0435\u043A\u0440\u0435\u0442\u043D\u0443\u044E \u0441\u0441\u044B\u043B\u043A\u0443" }), " \u0438\u043B\u0438", " ", _jsx(Strong, { children: "QR-\u043A\u043E\u0434" }), " \u043D\u0430 \u0434\u0440\u0443\u0433\u043E\u043C \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0435."] }) }), _jsxs(Alert, { open: errorMsg !== null, onClose: (open) => {
                    if (!open)
                        setErrorMsg(null);
                }, children: [_jsx(AlertTitle, { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0432\u043E\u0439\u0442\u0438" }), _jsx(AlertDescription, { children: errorMsg }), _jsx(AlertActions, { children: _jsx(Button, { onClick: () => setErrorMsg(null), children: "\u041F\u043E\u043D\u044F\u0442\u043D\u043E" }) })] }), _jsx("div", { children: _jsxs(Text, { className: "text-left", children: ["\u0425\u043E\u0442\u0438\u0442\u0435 \u0441\u043E\u0437\u0434\u0430\u0442\u044C \u043D\u043E\u0432\u044B\u0439 \u0430\u043A\u043A\u0430\u0443\u043D\u0442?", " ", _jsx(TextLink, { href: withReturnTo("/register", returnTo), children: "\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F" })] }) })] }));
};
export default Login;
